/**
 * Hook for stopping an active chat execution via REST API.
 * Sends POST to /api/v1/conversations/{id}/stop and listens for
 * execution_stopped WebSocket event to confirm completion.
 */

import { useStopConversationExecutionMutation } from '@saaga/shared/store/conversation-stop-endpoint.store'
import {
  clearConversationStopping,
  setConversationStopping,
} from '@saaga/shared/store/slices/chat.slice'
import {
  selectIsConversationStopping,
  selectIsOrchestrationActive,
} from '@saaga/shared/store/slices/chat-selectors.store'
import { useAppDispatch, useAppSelector } from '@saaga/shared/store/store'
import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'

const STOP_TIMEOUT_MS = 5000

interface UseChatStopExecutionParams {
  conversationId: string | null
}

export interface UseChatStopExecutionReturn {
  handleStop: () => void
  isStreaming: boolean
  isStopping: boolean
}

export const useChatStopExecution = ({
  conversationId,
}: UseChatStopExecutionParams): UseChatStopExecutionReturn => {
  const dispatch = useAppDispatch()
  const [stopExecution] = useStopConversationExecutionMutation()
  const isStreaming = useAppSelector((state) =>
    conversationId ? selectIsOrchestrationActive(state, conversationId) : false
  )
  const isStopping = useAppSelector((state) =>
    conversationId ? selectIsConversationStopping(state, conversationId) : false
  )
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (conversationId) dispatch(clearConversationStopping({ conversationId }))
    }
  }, [conversationId, dispatch])

  const handleStop = useCallback(() => {
    if (!conversationId || isStopping) return

    dispatch(setConversationStopping({ conversationId }))

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      dispatch(clearConversationStopping({ conversationId }))
    }, STOP_TIMEOUT_MS)

    stopExecution(conversationId)
      .unwrap()
      .catch(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        dispatch(clearConversationStopping({ conversationId }))
        toast.error('Failed to stop generation')
      })
  }, [conversationId, isStopping, dispatch, stopExecution])

  return { handleStop, isStreaming, isStopping }
}
